import { UscDisclaimerType } from "../../shared-logic/types/UscCommonTypes";
import type { DisclaimerType } from "../../../common-deprecated/types/CommonTypes";
import { DisclaimerLocation } from "../../../common-deprecated/types/CommonTypes";
import { COMPONENT_USED_STOCK_CARS_PDF } from "../../../shared-logic/server/components";
import {
    getUscCashPriceFromVehicle,
    getUscCashPriceValue,
    showUscInlinePriceDisclaimer,
    showUscPrice,
    uscPriceIsExclVat,
    uscPriceIsInclVat,
} from "../../shared-logic/utils/uscPriceUtils";
import { getCommonOptionalLabel } from "../../../common-deprecated/utils/commonLabels";
import { getVehiclePriceSource } from "./priceUtils";
import { enableUscInsurance, enableUscMonthlyRate } from "./settings";
import { getDisclaimerLinkLabel, UscQuickSpec } from "../../shared-logic/utils/uscUtils";
import { AemFmComponent } from "../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { getFeeListSelector } from "./externalPriceUtils";
import { UscNavStepEnum } from "./constants";
import type { UscStateType } from "../redux/store";
import { uscQuickSpecSelector, uscSpecDisclaimerSelector } from "./specUtils";
import { getInsuranceDisclaimerLinkLabel } from "./insuranceUtils";
import { DfFuelTypeId } from "../../shared-logic/utils/dfConstants";
import { getUscOptionalLabel } from "./labels";
import { getEcoCostsLabels } from "./ecoCostsUtils";
import { getUscPromotionDisclaimer } from "./promotionUtils";

/**
 * Returns a list of disclaimers that need to be referenced
 */
export const disclaimerSelector = (state: UscStateType): DisclaimerType<UscDisclaimerType>[] => {
    let disclaimers: DisclaimerType<UscDisclaimerType>[] = [];
    const { vehicle, basket, commonSettings, uscSettings, nav, accessories } = state;
    const { country } = commonSettings;

    const isPdf = commonSettings.component === COMPONENT_USED_STOCK_CARS_PDF;
    if (!vehicle.result) return [];

    // To get a somewhat 'consistent' numbering order (items higher up in the page having lower numbers) make sure the items that are usually rendered near the top are higher ordered here.

    // External price disclaimers (taxes, reservation fees,...)
    if (state.externalPrice.show) {
        [...getFeeListSelector("included")(state), ...getFeeListSelector("excluded")(state)].forEach((line) => {
            if (line.disclaimer) {
                disclaimers.push({
                    type: UscDisclaimerType.ExternalPrice,
                    reference: line.key,
                    value: line.disclaimer,
                    location: DisclaimerLocation.InfoIcon,
                });
            }
        });
    }

    // Cash price disclaimers
    const uscCashPrice = getUscCashPriceFromVehicle(state.vehicle.result);
    if (getUscCashPriceValue(uscCashPrice)) {
        const cashDisclaimer = uscPriceIsExclVat(uscCashPrice)
            ? getCommonOptionalLabel(state, "uscExclVatCashPriceDisclaimer")
            : uscPriceIsInclVat(uscCashPrice)
              ? getCommonOptionalLabel(state, "uscInclVatCashPriceDisclaimer")
              : getCommonOptionalLabel(state, "uscCashPriceDisclaimer");

        if (cashDisclaimer) {
            let location = DisclaimerLocation.InfoIcon;
            if (showUscInlinePriceDisclaimer(country)) location = DisclaimerLocation.Inline;

            disclaimers.push({ type: UscDisclaimerType.Cash, value: cashDisclaimer, location });
        }
    }

    // Accessory disclaimer
    if (accessories.initialized && (accessories.accessoryResults.length || accessories.packResults.length)) {
        const accessoryDisclaimer = getCommonOptionalLabel(state, "uscAccessoryCashPriceDisclaimer");

        if (accessoryDisclaimer) {
            disclaimers.push({
                type: UscDisclaimerType.Accessory,
                value: accessoryDisclaimer,
                location: DisclaimerLocation.InfoIcon,
            });
        }
    }

    // Promotion
    if (vehicle.result.promotion) {
        disclaimers.push({
            type: UscDisclaimerType.Promotion,
            value: getUscPromotionDisclaimer({
                state,
                promotionDisclaimer: vehicle.result.promotion,
                cultureName: commonSettings.culture.name,
            }),
            containsHtml: true,
            location: DisclaimerLocation.Footnote,
        });
    }

    // Finance
    if (
        showUscPrice(getVehiclePriceSource(state.vehicle.result)) &&
        enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type) &&
        state.finance.info?.disclaimer?.value &&
        // In the PDF we only show the finance disclaimer when the user selected monthly
        (isPdf ? basket.activeFinanceOption === "monthly" : true)
    ) {
        const disclaimerLabel = state.finance.info
            ? getDisclaimerLinkLabel(
                  commonSettings,
                  AemFmComponent.Pdp,
                  state.finance.monthlyPrice,
                  state.finance.info,
                  vehicle.type,
              )
            : "";

        let location = DisclaimerLocation.InfoIcon;
        if (showUscInlinePriceDisclaimer(country)) location = DisclaimerLocation.Inline;
        else if (disclaimerLabel) location = DisclaimerLocation.InfoText;

        disclaimers.push({
            type: UscDisclaimerType.Finance,
            value: state.finance.info?.disclaimer?.value,
            containsHtml: true,
            location,
            infoText: disclaimerLabel,
        });
    }

    // Insurance
    if (
        enableUscInsurance(commonSettings, uscSettings, vehicle.result, vehicle.type) &&
        state.insurance.quote?.disclaimer
    ) {
        const disclaimerLabel = getInsuranceDisclaimerLinkLabel(
            commonSettings,
            uscSettings,
            AemFmComponent.Pdp,
            state.insurance.quote,
            vehicle.type,
        );

        /**
         * Check if we should render the disclaimer as a footnote.
         * Otherwise, render it as an infoText (button with label) or an infoIcon.
         */
        let location = DisclaimerLocation.InfoIcon;
        if (showUscInlinePriceDisclaimer(country)) location = DisclaimerLocation.Inline;
        else if (disclaimerLabel) location = DisclaimerLocation.InfoText;

        disclaimers.push({
            type: UscDisclaimerType.Insurance,
            value: state.insurance.quote.disclaimer,
            location,
            infoText: disclaimerLabel,
            containsHtml: true,
        });
    }

    // Specs are not shown on extras.
    if (nav.step !== UscNavStepEnum.Extras) {
        // Get disclaimers for specs, and add them to the list.
        // Spec disclaimers also contain wltp/nedc disclaimers.
        const specDisclaimers = uscSpecDisclaimerSelector("all")(state).map((disclaimer) => ({
            ...disclaimer,
            location: DisclaimerLocation.InfoIcon,
            containsHtml: true,
        }));

        disclaimers = disclaimers.concat(specDisclaimers);
    }

    // Force footnotes for PDF disclaimers.
    if (isPdf) {
        disclaimers = disclaimers.map((disclaimer) => ({
            ...disclaimer,
            // Dont override inline price disclaimers as these are rendered next to the price anyway.
            location:
                showUscInlinePriceDisclaimer(country) && disclaimer.location === DisclaimerLocation.Inline
                    ? DisclaimerLocation.Inline
                    : DisclaimerLocation.Footnote,
        }));
    }

    // Add a number only for the footnote items to avoid 'invisible' numbers.
    let latestIndex = 0;
    return disclaimers.map((disclaimer) => {
        if (disclaimer.location === DisclaimerLocation.Footnote) {
            latestIndex++;
            disclaimer.number = latestIndex;
        }
        return disclaimer;
    });
};

/**
 * Wrapper for the disclaimer selector, allows to easily retrieve a specific disclaimer config.
 */
export const getUscDisclaimerSelector =
    (type: UscDisclaimerType, reference?: string) =>
    (state: UscStateType): DisclaimerType<UscDisclaimerType> | null => {
        const disclaimers = disclaimerSelector(state);

        return disclaimers.find((disclaimer) => disclaimer.type === type && disclaimer.reference === reference) || null;
    };

/**
 * The monthly price disclaimers are by default only shown when switched to monthly
 * NMSCs can enable 'showFinanceLabelsOnCash' to always show the finance disclaimers
 */
export const showUscMonthlyDisclaimersSelector = (state: UscStateType): boolean => {
    const { commonSettings, uscSettings, vehicle, basket } = state;
    const enableFinance = enableUscMonthlyRate(commonSettings, uscSettings, vehicle.result, vehicle.type);
    const { showFinanceLabelsOnCash } = uscSettings;
    return enableFinance && (basket.activeFinanceOption === "monthly" || showFinanceLabelsOnCash);
};

export const ecolabelDisclaimerSelector =
    (fuelType?: DfFuelTypeId) =>
    (state: UscStateType): DisclaimerType<UscDisclaimerType>[] => {
        if (!fuelType) return [];

        const { uscSettings, ecoCosts } = state;

        const { ecolabelTmefConfig } = uscSettings;
        const ecoLabels = getEcoCostsLabels(ecolabelTmefConfig, fuelType);

        if (!ecoCosts.data) return [];

        const specs = uscQuickSpecSelector("specCategories")(state);

        const disclaimers: DisclaimerType<UscDisclaimerType>[] = [];

        // The generic disclaimer should always get rendered if available, regardless of the fuel type (and without footnote number)
        const genericDisclaimer = getUscOptionalLabel(state, "uscEcolabelDisclaimer");
        if (genericDisclaimer) {
            disclaimers.push({
                type: UscDisclaimerType.Spec,
                value: genericDisclaimer,
                // Not relevant for this disclaimer, just needs to be anything other than footnote
                // Gets rendered as a footnote, but without a number
                location: DisclaimerLocation.InfoText,
                containsHtml: true,
            });
        }

        // The CO2 Emission disclaimer should only get rendered if the label is available and the combinedCo2 spec is available
        const co2EmissionDisclaimer = getUscOptionalLabel(state, "uscEcolabelCO2EmissionDisclaimer");
        const co2WltpCombinedSpec = specs.find((spec) => spec.type === UscQuickSpec.Co2WltpCombined);
        const co2CSCombinedSpec = specs.find((spec) => spec.type === UscQuickSpec.Co2CsCombined);
        if (co2EmissionDisclaimer && (co2WltpCombinedSpec || co2CSCombinedSpec)) {
            disclaimers.push({
                type: UscDisclaimerType.Spec,
                value: co2EmissionDisclaimer,
                location: DisclaimerLocation.Footnote,
                containsHtml: true,
            });
        }

        const futureCo2PricingDisclaimer = getUscOptionalLabel(state, "uscEcolabelFutureCO2PricingDisclaimer");
        if (futureCo2PricingDisclaimer && ecoLabels?.co2CostTitle) {
            disclaimers.push({
                type: UscDisclaimerType.Spec,
                value: futureCo2PricingDisclaimer
                    .replace("{disclaimerFrom}", ecoCosts.data.disclaimerFrom)
                    .replace("{disclaimerTo}", ecoCosts.data.disclaimerTo),
                location: DisclaimerLocation.Footnote,
                containsHtml: true,
            });
        }

        const electricTaxExemptionDisclaimer = getUscOptionalLabel(state, "uscEcolabelElectricTaxExemptionDisclaimer");
        if (
            electricTaxExemptionDisclaimer &&
            (fuelType === DfFuelTypeId.Electric || fuelType === DfFuelTypeId.Hydrogen) &&
            ecoLabels?.vehicleTaxTitle &&
            ecoCosts.data.taxYearly === 0
        ) {
            disclaimers.push({
                type: UscDisclaimerType.Spec,
                value: electricTaxExemptionDisclaimer,
                location: DisclaimerLocation.Footnote,
                containsHtml: true,
            });
        }

        let latestIndex = 0;
        return disclaimers.map((disclaimer) => {
            if (disclaimer.location === DisclaimerLocation.Footnote) {
                latestIndex++;
                disclaimer.number = latestIndex;
            }
            return disclaimer;
        });
    };

/**
 * Get a disclaimer id for usage in HTML ids or React keys.
 */
export const getDisclaimerId = (disclaimer: DisclaimerType<UscDisclaimerType>): string =>
    `${disclaimer.type}-${disclaimer?.number || ""}-${disclaimer?.reference || ""}`;
